import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import ImageUploader from '@/admin/ImageUploader';
import { TextField, Toggle, Section, Row, SaveBar, EyebrowSizeField } from '@/admin/AdminUI';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

const SECTION_KEYS = [
  { key: 'hero', label: 'Hero (Section 01)' },
  { key: 'experiences', label: 'Experiences (Section 02)' },
  { key: 'speakers', label: 'Speakers (Section 03)' },
  { key: 'registration', label: 'Registration (Section 04)' },
  { key: 'sponsors', label: 'Sponsors + Gallery (Section 05)' },
];

export default function SiteSettingsTab() {
  const [nav, setNav] = useState(null);
  const [meta, setMeta] = useState({});
  const [maintenance, setMaintenance] = useState({});
  const [globalCtas, setGlobalCtas] = useState({ sponsorship_url: '' });
  const [bottomMarquee, setBottomMarquee] = useState({ items: ['', '', '', ''], visible: true });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  // True when an edit made DURING an in-flight save was rotated into the
  // next patch: that edit is on screen but NOT on the server, so showing
  // "Saved ✓" would be false. Persists until a save completes with an
  // empty pending patch.
  const [pendingNotice, setPendingNotice] = useState(false);

  const load = async () => {
    const r = await api.get('/admin/settings');
    setNav(r.data?.nav || { links: [], register_label: 'Register Now', register_url: 'https://circle.swoogo.com/SEI_ConIII' });
    setMeta(r.data?.sections_meta || {});
    setMaintenance(r.data?.maintenance || { enabled: false });
    setGlobalCtas(r.data?.global_ctas || { sponsorship_url: '' });
    const bm = r.data?.bottom_marquee || {};
    const bmItems = Array.isArray(bm.items) && bm.items.length > 0
      ? bm.items
      : [
          'SEI CON \u2022 JULY 7\u20139, 2026',
          'SPORTS ENTERTAINMENT INNOVATION CONFERENCE',
          'SPORTS \u2022 ENTERTAINMENT \u2022 INNOVATION',
          'EDUCATION COLLABORATION DEALMAKING',
        ];
    setBottomMarquee({ items: bmItems, visible: bm.visible !== false });
  };
  useEffect(() => { load(); }, []);

  // Lost-update guard for sections_meta. This screen loads its copy of
  // the shared sections_meta blob ONCE at mount; only the fields THIS
  // session actually edits are recorded here and sent to the atomic
  // patch endpoint at save time, so a stale window can never overwrite
  // fields someone else changed. A `null` value means "remove field".
  //
  // HOOK ORDER: this ref MUST stay above the `if (!nav) return` early
  // return below — a hook after a conditional return changes the hook
  // count between renders and crashes the whole tab.
  const metaPatchRef = useRef({});
  const recordMetaPatch = (key, field, value) => {
    const cur = metaPatchRef.current[key] || {};
    metaPatchRef.current[key] = { ...cur, [field]: value };
    // A fresh sections-meta edit makes any visible "Saved ✓" stale the
    // instant it happens — clear it immediately, don't wait for a save.
    setSaved(false);
  };

  // Apply a recorded patch onto a base sections_meta object: null
  // removes the field, and a section emptied by removals is dropped.
  // Used to keep mid-save edits visible on top of the server's result.
  const applyMetaPatch = (base, patch) => {
    const merged = { ...base };
    for (const [key, p] of Object.entries(patch)) {
      const cur = { ...(merged[key] || {}) };
      for (const [f, v] of Object.entries(p)) {
        if (v === null) delete cur[f]; else cur[f] = v;
      }
      if (Object.keys(cur).length === 0) delete merged[key]; else merged[key] = cur;
    }
    return merged;
  };

  if (!nav) return <div className="font-body text-ink-muted">Loading…</div>;

  const links = Array.isArray(nav.links) ? nav.links : [];
  const setLink = (i, k, v) => {
    const next = [...links];
    next[i] = { ...next[i], [k]: v };
    setNav({ ...nav, links: next });
  };
  const addLink = () => setNav({ ...nav, links: [...links, { label: 'NEW', href: '#' }] });
  const removeLink = (i) => setNav({ ...nav, links: links.filter((_, idx) => idx !== i) });
  const moveLink = (i, dir) => {
    const swap = i + dir;
    if (swap < 0 || swap >= links.length) return;
    const next = [...links];
    [next[i], next[swap]] = [next[swap], next[i]];
    setNav({ ...nav, links: next });
  };

  const setVisible = (key, val) => {
    const cur = meta[key] || {};
    setMeta({ ...meta, [key]: { ...cur, visible: val } });
    recordMetaPatch(key, 'visible', val);
  };

  // v3.15 — FAQ eyebrow SIZE commit handler.
  // The FAQ eyebrow TEXT is intentionally hardcoded to "GOT
  // QUESTIONS?" (per SCOPE LOCK) — only its font-size is editable
  // and lives under `sections_meta.faq.eyebrow_size`.
  //
  // n === null → remove the key. If `sections_meta.faq` had no other
  // keys (which is the current baseline), we also remove the parent
  // `faq` subkey so the sections-meta doc stays byte-identical to
  // baseline when nothing has been overridden.
  const commitFaqEyebrowSize = (n) => {
    const nextMeta = { ...meta };
    const faq = { ...(nextMeta.faq || {}) };
    if (n === null) {
      delete faq.eyebrow_size;
      if (Object.keys(faq).length === 0) {
        delete nextMeta.faq;
      } else {
        nextMeta.faq = faq;
      }
    } else {
      faq.eyebrow_size = n;
      nextMeta.faq = faq;
    }
    setMeta(nextMeta);
    // null records an explicit removal for the save-time merge.
    recordMetaPatch('faq', 'eyebrow_size', n);
  };

  const setMaint = (k, v) => setMaintenance({ ...maintenance, [k]: v });

  const save = async () => {
    setSaving(true); setSaved(false); setSaveError('');
    // Snapshot semantics: the dirty patch is captured and ROTATED just
    // before the request, so toggles flipped while the request is in
    // flight land in a fresh patch — still visible on screen, queued
    // for the next Save, and never clobbered by this response.
    let snapshot = null;
    try {
      await api.put('/admin/settings/nav', nav);
      if (Object.keys(metaPatchRef.current).length > 0) {
        snapshot = metaPatchRef.current;
        metaPatchRef.current = {};
        // Atomic field-level patch: the server applies exactly these
        // fields in one Mongo update, so two admins patching different
        // fields both survive in either completion order.
        const r = await api.put('/admin/settings/sections-meta-patch', snapshot);
        const serverMeta = r.data?.sections_meta || {};
        // Display = the server's authoritative result plus any edits
        // made after the snapshot, so nothing on screen jumps back.
        setMeta(applyMetaPatch(serverMeta, metaPatchRef.current));
        snapshot = null; // consumed successfully
      }
      // An empty dirty patch makes ZERO sections_meta requests.
      await api.put('/admin/settings/maintenance', maintenance);
      await api.put('/admin/settings/global-ctas', globalCtas);
      await api.put('/admin/settings/bottom-marquee', bottomMarquee);
      if (Object.keys(metaPatchRef.current).length > 0) {
        // An edit landed while this save was in flight. It is queued for
        // the next Save but NOT on the server, so "Saved ✓" would be a
        // lie — show the persistent unsaved notice instead.
        setPendingNotice(true);
      } else {
        setPendingNotice(false);
        setSaved(true); setTimeout(() => setSaved(false), 2000);
      }
    } catch (e) {
      if (snapshot) {
        // The patch request failed: merge the submitted snapshot back
        // into whatever accumulated meanwhile, NEWER edits winning on
        // any field both touched, so Retry re-sends everything without
        // erasing later changes.
        const pending = metaPatchRef.current;
        const restored = {};
        for (const [k, p] of Object.entries(snapshot)) restored[k] = { ...p };
        for (const [k, p] of Object.entries(pending)) restored[k] = { ...(restored[k] || {}), ...p };
        metaPatchRef.current = restored;
      }
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) && detail[0]?.msg ? detail[0].msg : (typeof detail === 'string' ? detail : (e?.message || 'Save failed'));
      setSaveError(msg);
    } finally { setSaving(false); }
  };

  const setBottomMarqueeItem = (i, val) => {
    const next = Array.isArray(bottomMarquee.items) ? [...bottomMarquee.items] : ['', '', '', ''];
    next[i] = val;
    setBottomMarquee({ ...bottomMarquee, items: next });
  };

  return (
    <div>
      <Section title="Navigation Bar">
        <Row>
          <TextField label="Register Button Label" value={nav.register_label} onChange={(v) => setNav({ ...nav, register_label: v })} />
          <TextField label="Register Button URL" value={nav.register_url} onChange={(v) => setNav({ ...nav, register_url: v })} />
        </Row>
        <div className="flex items-center justify-between mt-4 mb-2">
          <div className="font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2">Nav Links</div>
          <button onClick={addLink} className="btn-primary text-[12px] !py-2 !px-4" data-testid="add-nav-link"><Plus size={14} className="mr-1" /> Add Link</button>
        </div>
        <div className="space-y-2">
          {links.map((l, i) => (
            <div key={i} className="flex items-center gap-2 p-2 border border-border-line rounded-sm bg-white" data-testid="nav-link-row">
              <TextField label="" value={l.label} onChange={(v) => setLink(i, 'label', v)} placeholder="Label" />
              <TextField label="" value={l.href} onChange={(v) => setLink(i, 'href', v)} placeholder="Anchor or URL (e.g. #speakers or /contact)" />
              <div className="flex flex-col gap-1">
                <button onClick={() => moveLink(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30"><ChevronUp size={14} /></button>
                <button onClick={() => moveLink(i, 1)} disabled={i === links.length - 1} className="p-1 disabled:opacity-30"><ChevronDown size={14} /></button>
              </div>
              <button onClick={() => removeLink(i)} className="btn-secondary !py-2 !px-3 text-[11px]"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Per-Section Visibility">
        <p className="font-body text-[12px] text-ink-muted mb-2">Hide a section to omit it from the homepage entirely (anchors will not scroll to it).</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SECTION_KEYS.map(({ key, label }) => (
            <Toggle key={key} label={`${label} visible`} value={(meta[key] || {}).visible !== false} onChange={(v) => setVisible(key, v)} testid={`section-visible-${key}`} />
          ))}
        </div>
      </Section>

      <Section title="FAQ — Section Labels">
        <p className="font-body text-[12px] text-ink-muted mb-2">
          The FAQ eyebrow text is intentionally locked to <span className="font-semibold">&quot;GOT QUESTIONS?&quot;</span> and is not editable here. You can, however, adjust its size independently of the other section eyebrows.
        </p>
        <EyebrowSizeField
          label="FAQ Eyebrow Size (px)"
          value={(meta.faq || {}).eyebrow_size}
          onCommit={commitFaqEyebrowSize}
          testid="faq-eyebrow-size"
          hint="Controls only the size of the orange &quot;GOT QUESTIONS?&quot; label above the FAQ accordion."
        />
      </Section>

      <Section title="Master Maintenance Mode">
        <Toggle label="Enable sitewide Coming Soon landing" value={!!maintenance.enabled} onChange={(v) => setMaint('enabled', v)} testid="maintenance-enabled" />
        {maintenance.enabled && (
          <div className="mt-2 p-3 border border-brand-orange/40 bg-brand-orange/5 rounded-sm font-body text-[12px] text-ink-primary">
            <b>Note:</b> All public routes will show the Coming Soon landing while this is enabled.
          </div>
        )}
        <Row>
          <TextField label="Eyebrow" value={maintenance.eyebrow} onChange={(v) => setMaint('eyebrow', v)} />
          <TextField label="Headline" value={maintenance.headline} onChange={(v) => setMaint('headline', v)} />
        </Row>
        <TextField label="Body" value={maintenance.body} onChange={(v) => setMaint('body', v)} multiline />
        <ImageUploader value={maintenance.image_url} onChange={(v) => setMaint('image_url', v)} label="Background Image (optional)" />
      </Section>

      <Section title="Global CTAs">
        <p className="font-body text-[12px] text-ink-muted mb-2">URLs shared across multiple sections of the site. Single source of truth — changing the value here updates every place that reads it.</p>
        <TextField
          label="Sponsorship Opportunities Label"
          value={globalCtas.sponsorship_label || ''}
          onChange={(v) => setGlobalCtas({ ...globalCtas, sponsorship_label: v })}
          placeholder="Sponsorship Opportunities"
          testid="global-ctas-sponsorship-label"
        />
        <p className="font-body text-[11px] text-ink-soft mt-1 mb-3">Text shown on the third hero CTA button. Leave blank to use the default &quot;Sponsorship Opportunities&quot;.</p>
        <TextField
          label="Sponsorship Opportunities URL"
          value={globalCtas.sponsorship_url || ''}
          onChange={(v) => setGlobalCtas({ ...globalCtas, sponsorship_url: v })}
          placeholder="https://… or mailto:…"
          testid="global-ctas-sponsorship-url"
        />
        <p className="font-body text-[11px] text-ink-soft mt-1">Leave blank to fall back to mailto:tiffanys@sei-con.org. Accepts https:// URLs or mailto: links.</p>
      </Section>

      <Section title="Bottom Marquee Strip">
        <p className="font-body text-[12px] text-ink-muted mb-2">A thin scrolling band pinned to the bottom of every public page. Item 1 renders in brand-orange; items 2–4 render in white. Items are separated by orange diamonds.</p>
        <Toggle
          label="Show bottom marquee strip site-wide"
          value={bottomMarquee.visible !== false}
          onChange={(v) => setBottomMarquee({ ...bottomMarquee, visible: v })}
          testid="bottom-marquee-visible"
        />
        <Row>
          <TextField
            label="Item 1 (Orange)"
            value={(bottomMarquee.items && bottomMarquee.items[0]) || ''}
            onChange={(v) => setBottomMarqueeItem(0, v)}
            testid="bottom-marquee-item-0"
          />
          <TextField
            label="Item 2"
            value={(bottomMarquee.items && bottomMarquee.items[1]) || ''}
            onChange={(v) => setBottomMarqueeItem(1, v)}
            testid="bottom-marquee-item-1"
          />
        </Row>
        <Row>
          <TextField
            label="Item 3"
            value={(bottomMarquee.items && bottomMarquee.items[2]) || ''}
            onChange={(v) => setBottomMarqueeItem(2, v)}
            testid="bottom-marquee-item-2"
          />
          <TextField
            label="Item 4"
            value={(bottomMarquee.items && bottomMarquee.items[3]) || ''}
            onChange={(v) => setBottomMarqueeItem(3, v)}
            testid="bottom-marquee-item-3"
          />
        </Row>
      </Section>

      {saveError && (
        <div className="mb-3 p-3 border border-brand-red/40 bg-brand-red/5 rounded-sm font-body text-[12px] text-brand-red" data-testid="site-settings-save-error">
          <b>Save error:</b> {saveError}
        </div>
      )}

      {pendingNotice && (
        <div className="mb-3 p-3 border border-brand-orange/40 bg-brand-orange/5 rounded-sm font-body text-[12px] text-ink-primary" data-testid="site-settings-unsaved-notice">
          Changes made while saving are not saved yet. Click Save Changes again.
        </div>
      )}

      <SaveBar onSave={save} saving={saving} saved={saved} />
    </div>
  );
}
